import { useMemo } from 'react';
import AppEnvironment from './environment/app-environment';
import { useAppSelector } from './ducks/hooks';
import { ETabId } from './services/tab-configurations/tab-types';
import { EQuotaAcknowledgementStatus } from './ducks/api-ducks/participation-details-duck/types-participation-details';

// Show or hide Filters depending on active tab
export const useShowFilters = () => {
  const activeTab = useAppSelector((state) => state.filters?.activeTab);
  return useMemo(() => {
    switch (activeTab) {
      case ETabId.Global:
      case ETabId.ImpersonationTab:
      case ETabId.PlanSetupTab:
      case ETabId.NotificationsAdminTab:
        return false;
      default:
        return true;
    }
  }, [activeTab]);
};

// Set the refresh time for user notifications depending on environment.
// Local 10 seconds, UAT/PPE 1 min, Prod 30 mins, default 100 mins
export const useGetNotifciationRefreshTime = () =>
  useMemo(() => {
    switch (AppEnvironment.env) {
      case 'local':
        return 10000; // 10 seconds
      case 'uat':
      case 'ppe':
      case 'fed-ppe':
        return 60000; // 1 min
      case 'prod':
      case 'fed-prod':
        return 1800000; // 30 mins
      default:
        // all non defined return 100 mins
        return 6000000; // 100 mins
    }
  }, []);

// Determine the default tab based on compliance requirements
export const useDefaultTab = (availableTabs: any[]) => {
  const participationDetails = useAppSelector((state) => state.participationDetails);
  const filters = useAppSelector((state) => state.filters);

  return useMemo(() => {
    if (availableTabs.length === 0) {
      return undefined;
    }

    // If participationDetails && filters states are not ready, return first available tab
    if (!(participationDetails && filters)) {
      return availableTabs[0]?.tabId;
    }

    const selectedParticipation = participationDetails.coreParticipations.find(
      (participation) =>
        filters.participationId === participation.participationId &&
        filters.selectedParticipationPartitionGroup?.partitionGroup === participation.fiscalYearId.toString()
    );

    // If the participation on the filters is not found, return first available tab
    if (!selectedParticipation) {
      return availableTabs[0]?.tabId;
    }

    // Check if plan is enabled but revenue release is disabled or quota acknowledgement is not done
    const isPlanEnabled = selectedParticipation.isPlanActive;
    const isRevenueReleaseDisabled = !selectedParticipation.isRevenueReleaseEnabled;
    const isQuotaNotAccepted = selectedParticipation.quotaAcknowledgementStatus !== EQuotaAcknowledgementStatus.QuotaAccepted;

    // If plan is enabled but either revenue release is disabled or quota is not accepted,
    // prioritize Compliance tab if it's available
    if (isPlanEnabled && (isRevenueReleaseDisabled || isQuotaNotAccepted)) {
      const complianceTab = availableTabs.find((tabItem) => tabItem.tabId === ETabId.ComplianceTab);
      if (complianceTab) {
        return ETabId.ComplianceTab;
      }
    }

    // Default to first available tab
    return availableTabs[0]?.tabId;
  }, [availableTabs, participationDetails, filters]);
};
